/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module DataGridModule
 */

import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    SimpleChanges,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import {
    selectValuesToDisplay,
} from 'ng/root-store/user-preferences/user-preferences.selectors';

import { Series } from 'ajs/modules/metrics/factories/series.factory';
import { MetricsDisplayValueType } from 'ng/root-store/user-preferences/user-preferences.state';

import './avi-metrics-cell.component.less';

/**
 * @description
 *     Grid cell component for showing metrics data with a gradient loader.
 * @author Zhiqian Liu
 */
@Component({
    selector: 'avi-metrics-cell',
    templateUrl: './avi-metrics-cell.component.html',
})
export class AviMetricsCellComponent implements OnDestroy, OnChanges {
    /**
     * The series data used for extract metrics pieces.
     */
    @Input()
    public series: Series;

    /**
     * Indicate if the Metric item wrapper around the Series data is loading.
     * This also works as a dummy flipping value to trigger the normalizeMetricsCellData pipe to
     * update presentable series values upon loading completes. We wanna avoid the legacy way of
     * checking data updates on every change detection cycle as done in chartValueController.
     * Therefore, to the pipe, its true / false value doesn't have any semantic meaning.
     */
    @Input()
    public isLoading: boolean;

    /**
     * Indicate whether there's data from the series. Updated upon loading completes.
     */
    public hasData = false;

    /**
     * The the display type for the series data according to the metrics selector.
     */
    public metricsDisplayValueType: MetricsDisplayValueType;

    /**
     * The decimal point of presented value based on units.
     * 'METRIC_COUNT': 0. Otherwise: 1.
     */
    public valueDecimalPoint = 0;

    public ngDestroySubject$ = new Subject<void>();

    constructor(store: Store) {
        store.select(selectValuesToDisplay)
            .pipe(takeUntil(this.ngDestroySubject$))
            .subscribe(valueType => this.metricsDisplayValueType = valueType);
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        const { isLoading } = changes;

        if (isLoading && !isLoading.currentValue) {
            // when metrics loading finishes, check series to see if data exist
            // and trigger the value pipe
            this.hasData = this.series?.hasData() || false;

            const units = this.series?.getUnits();

            this.valueDecimalPoint = !units || units === 'METRIC_COUNT' ? 0 : 1;
        }
    }

    /** @override */
    public ngOnDestroy(): void {
        this.ngDestroySubject$.next();
        this.ngDestroySubject$.complete();
    }
}
